import React from 'react';
import styled from 'styled-components';
import { colors } from '../../utils/colors'
import * as TypographyStyles from '../TypographyStyles';
import { fontFamily, purpleColors, slateColors } from '../ContentPages/AppLandingPage/styles';


const TextFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
`

const Instruction = styled.label<{ $isBackgroundDark?: boolean; $labelColor?: string }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$labelColor || slateColors[700]};
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
`;

const ErrorMessage = styled(TypographyStyles.BodySSemibold)`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`

export const Input = styled.input<{ 
  $hasError?: boolean;
  $borderColor?: string;
  $backgroundColor?: string;
  $textColor?: string;
  $placeholderColor?: string;
  $focusBorderColor?: string;
  $focusBackgroundColor?: string;
  $focusShadowColor?: string;
  $backdropFilter?: string;
}>`
  font-family: ${fontFamily};
  font-size: 1rem;
  font-weight: 400;
  padding: 0.875rem 1.25rem;
  border-radius: 0.75rem;
  border: 2px solid ${props => {
    if (props.$hasError) return '#ef4444';
    return props.$borderColor || slateColors[200];
  }};
  background: ${props => props.$backgroundColor || 'white'};
  ${props => props.$backdropFilter ? `backdrop-filter: ${props.$backdropFilter};` : ''}
  color: ${props => props.$textColor || slateColors[900]};
  transition: all 0.2s;
  flex: 1 1 auto; /* Allows the input to grow and shrink */
  align-self: stretch;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  width: 100%;

  @media (min-width: 640px) {
    padding: 1rem 1.5rem;
  }

  &::placeholder {
    color: ${props => props.$placeholderColor || slateColors[400]};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$focusBorderColor || purpleColors[500]};
    background: ${props => props.$focusBackgroundColor || props.$backgroundColor || 'white'};
    ${props => props.$focusShadowColor ? `box-shadow: 0 0 0 3px ${props.$focusShadowColor};` : `box-shadow: 0 0 0 3px ${purpleColors[100]};`}
  }

  &:active {
    border-color: ${props => props.$focusBorderColor || purpleColors[500]};
  }
  
  &:disabled {
    border: 2px solid ${slateColors[300]};   
    background: ${slateColors[100]}; 
    cursor: not-allowed;
    color: ${slateColors[400]};
  }
`;

interface TextFieldProps {
    instruction: string;
    dynamicPlaceholder: string;
    isDisabled: boolean;
    isError: boolean;
    errorMessage?: string;
    value: string;  
    isBackgroundDark?: boolean;
    labelColor?: string;
    borderColor?: string;
    backgroundColor?: string;
    textColor?: string;
    placeholderColor?: string;
    focusBorderColor?: string;
    focusBackgroundColor?: string;
    focusShadowColor?: string;
    backdropFilter?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const TextField: React.FC<TextFieldProps> = ({
  instruction,
  dynamicPlaceholder,
  isDisabled,
  isError,
  errorMessage,
  value,  // Receive value from props
  isBackgroundDark = true,
  labelColor,
  borderColor,
  backgroundColor,
  textColor,
  placeholderColor,
  focusBorderColor,
  focusBackgroundColor,
  focusShadowColor,
  backdropFilter,
  onChange,  // Receive onChange handler from props
}) => {
    return (
      <>
        <TextFieldContainer>
            {isError ? (
              <ErrorMessage>
                {errorMessage} {/* Show error message if isError is true */}
              </ErrorMessage>
            ) : (
              <Instruction $isBackgroundDark={isBackgroundDark} $labelColor={labelColor}>{instruction}</Instruction>
            )}
            <Input
              type="text"
              value={value}
              placeholder={!value ? dynamicPlaceholder : ''}
              disabled={isDisabled}
              $hasError={isError}
              $borderColor={borderColor}
              $backgroundColor={backgroundColor}
              $textColor={textColor}
              $placeholderColor={placeholderColor}
              $focusBorderColor={focusBorderColor}
              $focusBackgroundColor={focusBackgroundColor}
              $focusShadowColor={focusShadowColor}
              $backdropFilter={backdropFilter}
              onChange={onChange}
            />
        </TextFieldContainer>
      </>
    );
};
  

const TextArea = styled.textarea<{ 
  $hasError?: boolean;
  $borderColor?: string;
  $backgroundColor?: string;
  $textColor?: string;
  $placeholderColor?: string;
  $focusBorderColor?: string;
  $focusBackgroundColor?: string;
  $focusShadowColor?: string;
  $backdropFilter?: string;
}>`
  font-family: ${fontFamily};
  font-size: 1rem;
  font-weight: 400;
  padding: 0.875rem 1.25rem;
  border-radius: 0.75rem;
  border: 2px solid ${props => {
    if (props.$hasError) return '#ef4444';
    return props.$borderColor || slateColors[200];
  }};
  background: ${props => props.$backgroundColor || 'white'};
  ${props => props.$backdropFilter ? `backdrop-filter: ${props.$backdropFilter};` : ''}
  color: ${props => props.$textColor || slateColors[900]};
  transition: all 0.2s;
  flex: 1 1 auto; /* Allows the textarea to grow and shrink */
  align-self: stretch;
  resize: vertical; /* Allow vertical resizing only */
  min-height: 8rem;
  box-sizing: border-box;
  width: 100%;

  @media (min-width: 640px) {
    padding: 1rem 1.5rem;
  }

  &::placeholder {
    color: ${props => props.$placeholderColor || slateColors[400]};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$focusBorderColor || purpleColors[500]};
    background: ${props => props.$focusBackgroundColor || props.$backgroundColor || 'white'};
    ${props => props.$focusShadowColor ? `box-shadow: 0 0 0 3px ${props.$focusShadowColor};` : `box-shadow: 0 0 0 3px ${purpleColors[100]};`}
  }

  &:active {
    border-color: ${props => props.$focusBorderColor || purpleColors[500]};
  }  
  
  &:disabled {
    border: 2px solid ${slateColors[300]};   
    background: ${slateColors[100]}; 
    cursor: not-allowed;
    color: ${slateColors[400]};
  }
`;

interface MultiLineTextFieldProps {
  instruction: string;
  dynamicPlaceholder: string;
  isDisabled: boolean;
  isError: boolean;
  errorMessage?: string;
  value: string;
  isBackgroundDark?: boolean;
  labelColor?: string;
  borderColor?: string;
  backgroundColor?: string;
  textColor?: string;
  placeholderColor?: string;
  focusBorderColor?: string;
  focusBackgroundColor?: string;
  focusShadowColor?: string;
  backdropFilter?: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;  // Update this to handle textarea events
}

export const MultiLineTextField: React.FC<MultiLineTextFieldProps> = ({    
    instruction,
    dynamicPlaceholder,
    isDisabled,
    isError,
    errorMessage,
    value,
    isBackgroundDark = true,
    labelColor,
    borderColor,
    backgroundColor,
    textColor,
    placeholderColor,
    focusBorderColor,
    focusBackgroundColor,
    focusShadowColor,
    backdropFilter,
    onChange,
  }) => {  
    return (
      <TextFieldContainer>
        {isError ? (
          <ErrorMessage>
            {errorMessage} {/* Show error message if isError is true */}
          </ErrorMessage>
        ) : (
          <Instruction $isBackgroundDark={isBackgroundDark} $labelColor={labelColor}>{instruction}</Instruction>
        )}
        <TextArea
          value={value}
          placeholder={!value ? dynamicPlaceholder : ''}
          disabled={isDisabled}
          $hasError={isError}
          $borderColor={borderColor}
          $backgroundColor={backgroundColor}
          $textColor={textColor}
          $placeholderColor={placeholderColor}
          $focusBorderColor={focusBorderColor}
          $focusBackgroundColor={focusBackgroundColor}
          $focusShadowColor={focusShadowColor}
          $backdropFilter={backdropFilter}
          onChange={onChange}
        />
      </TextFieldContainer>
    );
};